import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { api, fmtErr } from "../api";
import { ShieldCheck, CreditCard, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const RAZORPAY_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RAZORPAY_SRC;
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("Could not load Razorpay checkout. Check your connection."));
    document.body.appendChild(s);
  });
}

export default function PaymentModal({ open, onOpenChange, verificationId, onSuccess }) {
  const [stage, setStage] = useState("idle"); // idle | processing | success

  const finishSuccess = () => {
    setStage("success");
    setTimeout(() => {
      onSuccess();
      setStage("idle");
    }, 900);
  };

  const pay = async () => {
    setStage("processing");
    try {
      const { data: order } = await api.post("/payments/create-order", { verification_id: verificationId });

      if (order.mode === "live") {
        // Real Razorpay checkout
        try {
          await loadRazorpayScript();
        } catch (e) {
          setStage("idle");
          toast.error(fmtErr(e));
          return;
        }
        onOpenChange(false); // hand over to Razorpay's own modal
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          name: "PropLoX",
          description: "Title Scrutiny Report",
          order_id: order.order_id,
          theme: { color: "#0052FF" },
          handler: async (resp) => {
            try {
              await api.post("/payments/verify", {
                order_id: resp.razorpay_order_id,
                payment_id: resp.razorpay_payment_id,
                signature: resp.razorpay_signature,
                verification_id: verificationId,
              });
              toast.success("Payment successful — your report is unlocked.");
              onSuccess();
            } catch (e) {
              toast.error(fmtErr(e));
            }
          },
          modal: { ondismiss: () => toast.info("Payment cancelled. Your report stays locked until payment.") },
        });
        rzp.on("payment.failed", (resp) => {
          toast.error(resp?.error?.description || "Payment failed. Please try again.");
        });
        rzp.open();
        setStage("idle");
        return;
      }

      // Mock mode (test environment)
      await new Promise((r) => setTimeout(r, 1400));
      await api.post("/payments/verify", { order_id: order.order_id, verification_id: verificationId, payment_id: "", signature: "" });
      finishSuccess();
    } catch (e) {
      setStage("idle");
      toast.error(fmtErr(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (stage !== "processing") { onOpenChange(v); if (!v) setStage("idle"); } }}>
      <DialogContent className="max-w-sm p-0" data-testid="payment-modal">
        <DialogTitle className="sr-only">PropLoX Report Payment</DialogTitle>
        <div className="rounded-t-lg bg-slate-950 px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-blue-400">Razorpay Secure Checkout</p>
              <p className="mt-1 text-lg font-bold">PropLoX Title Scrutiny Report</p>
            </div>
            <ShieldCheck className="h-8 w-8 text-blue-400" />
          </div>
        </div>
        <div className="px-6 py-6">
          <div className="border-b border-dashed border-slate-200 pb-4">
            <p className="text-sm font-medium text-slate-700">One-time report unlock fee</p>
            <p className="mt-1 text-xs text-slate-400">Charged once per verification report.</p>
          </div>
          {stage === "success" ? (
            <div className="flex flex-col items-center py-8" data-testid="payment-success">
              <CheckCircle2 className="h-12 w-12 text-emerald-500" />
              <p className="mt-3 font-semibold text-slate-900">Payment successful</p>
              <p className="text-sm text-slate-500">Your report is unlocked.</p>
            </div>
          ) : (
            <>
              <div className="mt-5 flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <CreditCard className="h-5 w-5 text-slate-400" />
                <div className="text-sm">
                  <p className="font-medium text-slate-800">UPI · Cards · NetBanking · Wallets</p>
                  <p className="text-xs text-slate-500">256-bit encrypted payment by Razorpay.</p>
                </div>
              </div>
              <Button className="mt-5 w-full" size="lg" disabled={stage === "processing"} onClick={pay} data-testid="payment-pay-button">
                {stage === "processing" ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing…</>) : "Pay & Unlock Report"}
              </Button>
              <p className="mt-3 text-center text-[11px] text-slate-400">Secured by Razorpay</p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
